/**
 * useCascadingGeoFields Hook
 *
 * A React hook for managing cascading country → province → city field dependencies
 * using database-backed GeoDataService. Provides smart loading and automatic cascade clearing.
 *
 * Usage Examples:
 * 1. Country only: useCascadingGeoFields(['country'])
 * 2. Country + Province: useCascadingGeoFields(['country', 'province'])
 * 3. Full hierarchy: useCascadingGeoFields(['country', 'province', 'city'])
 *
 * Updated: January 2026 - Migrated to database-backed GeoDataService
 */

import { useState, useEffect, useCallback, useMemo } from 'react';
import {
  CascadingGeoFieldsConfig,
  CascadingGeoFieldsState,
  GeoFieldLevel,
  SelectOption,
} from '@xanectech/types';
import { GeoDataService } from '../services/GeoDataService';

import { createLogger } from '@xanectech/lib';

const logger = createLogger('Data - useCascadingGeoFields');

export const useCascadingGeoFields = (config: CascadingGeoFieldsConfig) => {
  const {
    levels,
    initialValues = {},
    onFieldChange,
    autoLoadOnMount = true,
  } = config;

  // State for field values
  const [values, setValues] = useState<Record<GeoFieldLevel, string>>(() => ({
    country: initialValues.country || '',
    province: initialValues.province || '',
    city: initialValues.city || '',
  }));

  // State for field options
  const [options, setOptions] = useState<Record<GeoFieldLevel, SelectOption[]>>(
    {
      country: [],
      province: [],
      city: [],
    },
  );

  // State for loading indicators
  const [loading, setLoading] = useState<Record<GeoFieldLevel, boolean>>({
    country: false,
    province: false,
    city: false,
  });

  // State for errors
  const [errors, setErrors] = useState<Record<GeoFieldLevel, string | null>>({
    country: null,
    province: null,
    city: null,
  });

  // Helper to set loading state for a specific level
  const setLoadingForLevel = useCallback(
    (level: GeoFieldLevel, isLoading: boolean) => {
      setLoading((prev) => ({ ...prev, [level]: isLoading }));
    },
    [],
  );

  // Helper to set error for a specific level
  const setErrorForLevel = useCallback(
    (level: GeoFieldLevel, error: string | null) => {
      setErrors((prev) => ({ ...prev, [level]: error }));
    },
    [],
  );

  // Load options for a specific level
  const loadOptionsForLevel = useCallback(
    async (level: GeoFieldLevel) => {
      setLoadingForLevel(level, true);
      setErrorForLevel(level, null);

      try {
        let levelOptions: SelectOption[] = [];

        switch (level) {
          case 'country':
            levelOptions = await GeoDataService.getCountryOptions();
            break;
          case 'province':
            levelOptions = await GeoDataService.getStateOptions(values.country);
            break;
          case 'city':
            levelOptions = await GeoDataService.getCityOptions(
              values.country,
              values.province,
            );
            break;
        }

        setOptions((prev) => ({ ...prev, [level]: levelOptions }));
      } catch (error) {
        logger.error(`Error loading ${level} options:`, {
          error: error instanceof Error ? error.message : String(error),
          stack: error instanceof Error ? error.stack : undefined,
        });
        setErrorForLevel(level, `Failed to load ${level} options`);
        setOptions((prev) => ({ ...prev, [level]: [] }));
      } finally {
        setLoadingForLevel(level, false);
      }
    },
    [values.country, values.province, setLoadingForLevel, setErrorForLevel],
  );

  // Clear dependent fields when parent changes
  const clearDependentFields = useCallback(
    (changedLevel: GeoFieldLevel) => {
      const levelIndex = levels.indexOf(changedLevel);
      const dependentLevels = levels.slice(levelIndex + 1);

      setValues((prev) => {
        const newValues = { ...prev };
        dependentLevels.forEach((level) => {
          newValues[level] = '';
        });
        return newValues;
      });

      // Clear options for dependent levels
      setOptions((prev) => {
        const newOptions = { ...prev };
        dependentLevels.forEach((level) => {
          newOptions[level] = [];
        });
        return newOptions;
      });
    },
    [levels],
  );

  // Handle field value changes
  const handleFieldChange = useCallback(
    (level: GeoFieldLevel, value: string) => {
      // Update the value
      setValues((prev) => ({ ...prev, [level]: value }));

      // Clear dependent fields
      clearDependentFields(level);

      // Load options for next level if it exists and has a value
      if (value) {
        const levelIndex = levels.indexOf(level);
        const nextLevel = levels[levelIndex + 1];

        if (nextLevel) {
          // Small delay to ensure state is updated
          setTimeout(() => {
            void loadOptionsForLevel(nextLevel);
          }, 0);
        }
      }

      // Call external change handler
      onFieldChange?.(level, value);
    },
    [levels, clearDependentFields, loadOptionsForLevel, onFieldChange],
  );

  // Check if a field should be enabled
  const isFieldEnabled = useCallback(
    (level: GeoFieldLevel): boolean => {
      const levelIndex = levels.indexOf(level);

      // Country is always enabled if it's in the levels
      if (level === 'country') return true;

      // Other levels are enabled if their parent has a value
      const parentLevel = levels[levelIndex - 1];
      return parentLevel ? Boolean(values[parentLevel]) : false;
    },
    [levels, values],
  );

  // Get options for a specific level
  const getOptionsForLevel = useCallback(
    (level: GeoFieldLevel): SelectOption[] => {
      return options[level] || [];
    },
    [options],
  );

  // Check if any field is loading
  const isLoading = useMemo(() => {
    return levels.some((level) => loading[level]);
  }, [levels, loading]);

  // Check if there are any errors
  const hasErrors = useMemo(() => {
    return levels.some((level) => errors[level] !== null);
  }, [levels, errors]);

  // Get current state summary
  const state: CascadingGeoFieldsState = useMemo(
    () => ({
      values,
      options,
      loading,
      errors,
    }),
    [values, options, loading, errors],
  );

  // Load initial data on mount
  useEffect(() => {
    if (autoLoadOnMount && levels.includes('country')) {
      void loadOptionsForLevel('country');
    }
  }, [autoLoadOnMount, levels, loadOptionsForLevel]);

  // Load province options if country has initial value
  useEffect(() => {
    if (initialValues.country && levels.includes('province')) {
      void loadOptionsForLevel('province');
    }
  }, [initialValues.country, levels, loadOptionsForLevel]);

  // Load city options if province has initial value
  useEffect(() => {
    if (initialValues.province && levels.includes('city')) {
      void loadOptionsForLevel('city');
    }
  }, [initialValues.province, levels, loadOptionsForLevel]);

  return {
    // Current state
    state,
    values,
    options,
    loading,
    errors,

    // Field management
    handleFieldChange,
    isFieldEnabled,
    getOptionsForLevel,

    // Utility functions
    loadOptionsForLevel,
    clearDependentFields,

    // Status indicators
    isLoading,
    hasErrors,

    // Configuration
    levels,
  };
};

// Convenience hooks for common patterns
export const useCountryField = (
  initialValue?: string,
  onChange?: (country: string) => void,
) => {
  return useCascadingGeoFields({
    levels: ['country'],
    initialValues: { country: initialValue || '' },
    onFieldChange: (level, value) => {
      if (level === 'country') onChange?.(value as string);
    },
  });
};

export const useCountryProvinceFields = (
  initialValues?: { country?: string; province?: string },
  onChange?: (values: { country: string; province: string }) => void,
) => {
  return useCascadingGeoFields({
    levels: ['country', 'province'],
    initialValues: initialValues || {},
    onFieldChange: () => {
      // Note: onChange callback would need to be handled externally
      // since we can't access hook state from callback context
      onChange?.({ country: '', province: '' });
    },
  });
};

export const useFullGeoFields = (
  initialValues?: { country?: string; province?: string; city?: string },
  onChange?: (values: {
    country: string;
    province: string;
    city: string;
  }) => void,
) => {
  return useCascadingGeoFields({
    levels: ['country', 'province', 'city'],
    initialValues: initialValues || {},
    onFieldChange: () => {
      // Note: onChange callback would need to be handled externally
      // since we can't access hook state from callback context
      onChange?.({ country: '', province: '', city: '' });
    },
  });
};
